import clsx from 'clsx';
import { Building2, Check, Copy, CreditCard, Loader2, QrCode, ShieldCheck } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCompleteCheckout, useReportTransfer, useStartCheckout, useWorkspace } from '@/api';
import type { CheckoutSession, Invoice, LegalSchema, TransferDetails } from '@/api/types';
import { fill, hasActs, providerOf } from '@/lib/contour';
import { balanceOf, formatDate, formatMoney } from '@/lib/format';
import { methodLabel } from '@/lib/status';
import { ConfirmDialog } from './ConfirmDialog';
import { Dialog, Meta } from './ui';

// Requisites come from the contour config through the workspace. Only the RU
// contour has a transfer (a Russian bank account: ИНН, БИК, корр. счёт).
const requisitesOf = (t: TransferDetails) => [
  { label: 'Получатель', value: t.payee },
  { label: 'ИНН', value: t.taxId },
  { label: 'Счёт', value: t.account },
  { label: 'Банк', value: t.bank },
  { label: 'БИК', value: t.bic },
  { label: 'Корр. счёт', value: t.correspondentAccount },
];

// The same words the bank gets from the portal: dd.mm.yyyy, as in a payment order.
const purposeOf = (inv: Invoice, legal?: LegalSchema) =>
  fill(legal?.paymentPurpose ?? 'Оплата по счёту № {number} от {date}', { number: inv.number, date: new Date(inv.issuedAt).toLocaleDateString('ru-RU', { timeZone: 'UTC' }) });

/** Payment order QR in the Russian banking standard (ГОСТ Р 56042-2014), readable by any bank app. Only where the contour declares it. */
function transferQrPayload(inv: Invoice, t: TransferDetails, legal?: LegalSchema): string | null {
  if (legal?.transferQr !== 'ST00012') return null;
  return ['ST00012', `Name=${t.payee}`, `PersonalAcc=${t.account}`, `BankName=${t.bank}`, `BIC=${t.bic}`, `CorrespAcc=${t.correspondentAccount}`, `PayeeINN=${t.taxId}`, `Sum=${balanceOf(inv).amountMinor}`, `Purpose=${purposeOf(inv, legal)}`].join('|');
}

/**
 * Always dark modules on white with a quiet zone: bank apps do not read an inverted
 * code (the dark theme used to draw light-on-transparent), and the box never shrinks
 * in a flex row, so the code stays square.
 */
function Qr({ payload, label }: { payload: string; label: string }) {
  const [src, setSrc] = useState<string>();
  useEffect(() => {
    QRCode.toDataURL(payload, { margin: 2, width: 480, errorCorrectionLevel: 'M', color: { dark: '#000000', light: '#ffffff' } }).then(setSrc);
  }, [payload]);
  return (
    <div className="frame grid size-48 shrink-0 place-items-center p-2">
      {src ? <img src={src} alt={label} className="aspect-square size-full rounded-sm bg-white object-contain [image-rendering:pixelated]" /> : <Loader2 className="size-5 animate-spin text-muted" />}
    </div>
  );
}

type Step = 'choose' | 'card' | 'sbp' | 'transfer' | 'paying' | 'redirect' | 'paid' | 'transfer-sent';

const allMethods = [
  { value: 'sbp', icon: QrCode, title: 'СБП', hint: 'QR-код в приложении банка, зачисление сразу' },
  { value: 'card', icon: CreditCard, title: 'Картой', hint: 'Любая карта, чек придёт на почту' },
  { value: 'transfer', icon: Building2, title: 'Переводом по счёту', hint: 'С расчётного счёта компании, зачисление после сверки с выпиской' },
] as const;
type Method = (typeof allMethods)[number]['value'];

export function PayDialog({ invoice, open, onClose }: { invoice: Invoice; open: boolean; onClose: () => void }) {
  const { data: ws } = useWorkspace();
  const opts = ws?.contour.payments;
  const legal = ws?.contour.legal;
  const acts = hasActs(ws?.contour);
  const provider = providerOf(ws?.contour);
  const transferTo = opts?.transfer ?? null;
  const qr = transferTo ? transferQrPayload(invoice, transferTo, legal) : null;
  const methods = allMethods
    .filter((m) => (m.value === 'transfer' ? !!transferTo : opts?.online.includes(m.value)))
    // Paddle mails its own invoice and receipt; «чек» is the RU (54-ФЗ) word.
    .map((m) => (m.value === 'card' && opts?.provider === 'paddle' ? { ...m, hint: 'Любая карта; счёт и квитанцию пришлёт Paddle' } : m));
  const [step, setStep] = useState<Step>('choose');
  const [picked, setMethod] = useState<Method>();
  const method = picked && methods.some((m) => m.value === picked) ? picked : methods[0]?.value;
  const [session, setSession] = useState<CheckoutSession>();
  const [copied, setCopied] = useState(false);
  const start = useStartCheckout();
  const complete = useCompleteCheckout();
  const transfer = useReportTransfer();
  const [paid, setPaid] = useState<Invoice>();
  const [confirmingTransfer, setConfirmingTransfer] = useState(false);
  // The invoice carries no «transfer reported» date; show one only if the API ever sends it.
  const reportedAt = transfer.data?.payment?.at;

  useEffect(() => {
    if (open) {
      setStep('choose');
      setSession(undefined);
      setPaid(undefined);
    }
  }, [open]);

  const amount = formatMoney(balanceOf(invoice));
  const next = () => {
    if (!method) return;
    if (method === 'transfer') return setStep('transfer');
    start.mutate(
      { invoiceId: invoice.id, method },
      {
        onSuccess: (s) => {
          setSession(s);
          if (s.redirectUrl) {
            // Card and SBP are paid on the bank's page; it sends the client back to this invoice.
            setStep('redirect');
            window.location.assign(s.redirectUrl);
            return;
          }
          setStep(method);
        },
      },
    );
  };
  const finish = () => {
    if (!session) return;
    setStep('paying');
    complete.mutate(session.id, {
      onSuccess: (inv) => {
        setPaid(inv);
        setStep('paid');
      },
    });
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} title={step === 'paid' ? 'Оплачено' : `Оплата счёта № ${invoice.number}`}>
      <div className="p-5">
        {step !== 'paid' && step !== 'transfer-sent' && (
          <div className="mb-5 flex items-baseline justify-between">
            <span className="text-sm text-muted">К оплате</span>
            <span className="num text-xl font-semibold tracking-tight">{amount}</span>
          </div>
        )}

        {step === 'choose' && methods.length === 0 && (
          <p className="text-sm text-muted">Оплата через кабинет здесь пока не подключена. Реквизиты — в счёте PDF, после перевода напишите менеджеру.</p>
        )}

        {step === 'choose' && methods.length > 0 && (
          <>
            <div className="space-y-2" role="radiogroup" aria-label="Способ оплаты">
              {methods.map((m) => (
                <button
                  key={m.value}
                  role="radio"
                  aria-checked={method === m.value}
                  onClick={() => setMethod(m.value)}
                  className={clsx('flex w-full items-start gap-3 rounded p-3 text-left transition-shadow', method === m.value ? 'shadow-[0_0_0_2px_rgb(var(--ink))]' : 'shadow-hair hover:shadow-[0_0_0_1px_rgb(var(--strong))]')}
                >
                  <m.icon className="mt-0.5 size-5 shrink-0" />
                  <span>
                    <span className="block font-medium">{m.title}</span>
                    <span className="block text-sm text-muted">{m.hint}</span>
                  </span>
                </button>
              ))}
            </div>
            {start.error && <p className="mt-3 text-sm text-overdue">{(start.error as Error).message}</p>}
            <button className="btn-primary mt-5 h-10 w-full" onClick={next} disabled={start.isPending}>
              {start.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Продолжить
            </button>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted">
              <ShieldCheck className="size-3.5" /> Платёж принимает {opts?.provider === 'paddle' ? 'Paddle' : 'банк'}, данные карты кабинет не видит
            </p>
          </>
        )}

        {step === 'sbp' && session?.qrPayload && (
          <div className="flex flex-col items-center text-center">
            <Qr payload={session.qrPayload} label="QR-код для оплаты через СБП" />
            <p className="mt-4 max-w-xs text-sm">Откройте приложение своего банка, выберите оплату по QR и наведите камеру.</p>
            <a href={session.qrPayload} className="mt-1 text-sm text-muted underline underline-offset-2 sm:hidden">
              Открыть в приложении банка
            </a>
            <button className="btn-primary mt-5 h-10 w-full" onClick={finish}>
              Я оплатил
            </button>
            <button className="btn-ghost mt-2 w-full" onClick={() => setStep('choose')}>
              Другой способ
            </button>
          </div>
        )}

        {step === 'card' && (
          <div>
            <div className="rounded-lg bg-panel p-4 shadow-hair">
              <div className="text-xs text-muted">Страница оплаты {provider.page}, демо</div>
              <div className="mt-3 space-y-2" aria-hidden>
                <div className="h-9 rounded bg-canvas shadow-hair" />
                <div className="grid grid-cols-2 gap-2">
                  <div className="h-9 rounded bg-canvas shadow-hair" />
                  <div className="h-9 rounded bg-canvas shadow-hair" />
                </div>
              </div>
              <p className="mt-3 text-xs text-muted">В живой версии здесь откроется защищённая страница банка: номер карты вводится там, кабинет его не получает.</p>
            </div>
            <button className="btn-primary mt-5 h-10 w-full" onClick={finish}>
              Оплатить {amount}
            </button>
            <button className="btn-ghost mt-2 w-full" onClick={() => setStep('choose')}>
              Другой способ
            </button>
          </div>
        )}

        {step === 'redirect' && (
          <div className="grid place-items-center py-10 text-center">
            <Loader2 className="size-6 animate-spin" />
            <p className="mt-3 text-sm text-muted">Открываем страницу оплаты {provider.page}…</p>
            {session?.redirectUrl && (
              <a href={session.redirectUrl} className="mt-2 text-sm underline underline-offset-2">
                Открыть вручную
              </a>
            )}
          </div>
        )}

        {step === 'transfer' && transferTo && (
          <div>
            {qr && (
              <div className="mb-4 flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:gap-5">
                <Qr payload={qr} label="QR-код с реквизитами для перевода" />
                <p className="text-sm text-muted">Отсканируйте в приложении банка — реквизиты, сумма и назначение заполнятся сами. Или скопируйте реквизиты.</p>
              </div>
            )}
            <div>
              <Meta items={[...requisitesOf(transferTo), { label: 'Назначение', value: purposeOf(invoice, legal) }]} />
            </div>
            <div className="mt-4 flex gap-2">
              <button
                className="btn-secondary flex-1"
                onClick={async () => {
                  await navigator.clipboard.writeText(`${requisitesOf(transferTo).map((r) => `${r.label}: ${r.value}`).join('\n')}\nНазначение: ${purposeOf(invoice, legal)}\nСумма: ${amount}`).catch(() => undefined);
                  setCopied(true);
                }}
              >
                <Copy className="size-4" /> {copied ? 'Скопировано' : 'Скопировать реквизиты'}
              </button>
              <button className="btn-primary flex-1" disabled={transfer.isPending} onClick={() => setConfirmingTransfer(true)}>
                Я отправил перевод
              </button>
            </div>
            {transfer.error && (
              <p role="alert" className="mt-3 text-sm text-overdue">
                {(transfer.error as Error).message}
              </p>
            )}
          </div>
        )}

        {step === 'paying' && (
          <div className="grid place-items-center py-10 text-center">
            <Loader2 className="size-6 animate-spin" />
            <p className="mt-3 text-sm text-muted">{provider.who} подтверждает платёж…</p>
          </div>
        )}

        {step === 'paid' && paid && (
          <div className="text-center">
            <div className="frame mx-auto grid size-14 place-items-center">
              <Check className="size-6 text-done" strokeWidth={2.5} />
            </div>
            <p className="mt-4 text-lg font-semibold">
              {formatMoney(balanceOf(invoice))} {paid.payment ? methodLabel[paid.payment.method] : ''}
            </p>
            <p className="mt-1 text-sm text-muted">
              {paid.payment?.receipt ?? 'Платёж зачислен'}.{acts && ' Акт по этому счёту уже готов и ждёт вашей подписи.'}
            </p>
            <div className="mt-5 flex gap-2">
              <button className={acts ? 'btn-secondary flex-1' : 'btn-primary flex-1'} onClick={onClose}>
                Готово
              </button>
              {acts && (
                <Link className="btn-primary flex-1" to="/money?tab=acts" onClick={onClose}>
                  Подписать акт
                </Link>
              )}
            </div>
          </div>
        )}

        {step === 'transfer-sent' && (
          <div className="text-center">
            <Loader2 className="mx-auto size-6 animate-spin text-muted" />
            <p className="mt-4 font-semibold">Проверяем поступление</p>
            <p className="mt-1 text-sm text-muted">Как только деньги придут по выписке банка, счёт отметится оплаченным{acts && ' и появится акт'}. </p>
            <p className="mt-2 text-sm text-muted">Студия сверяет переводы вручную по выписке банка, обычно в течение 1–2 рабочих дней.</p>
            <p className="mt-2 text-sm">{reportedAt ? `Вы сообщили о переводе ${formatDate(reportedAt)}` : 'Перевод отмечен'}</p>
            <Link className="btn-ghost mt-3 w-full" to={invoice.projectId ? `/p/${invoice.projectId}/talk` : '/inbox'} onClick={onClose}>
              Написать менеджеру
            </Link>
            <button className="btn-secondary mt-5 w-full" onClick={onClose}>
              Понятно
            </button>
          </div>
        )}
      </div>
    </Dialog>
    <ConfirmDialog
      open={confirmingTransfer}
      onClose={() => setConfirmingTransfer(false)}
      title="Вы отправили перевод?"
      confirmLabel="Да, я отправил перевод"
      pending={transfer.isPending}
      onConfirm={() => transfer.mutate(invoice.id, { onSuccess: () => setStep('transfer-sent'), onSettled: () => setConfirmingTransfer(false) })}
    >
      Счёт № {invoice.number} на {amount}. Студия сверит перевод с выпиской и закроет счёт: сообщайте только о переводе, который уже ушёл из банка.
    </ConfirmDialog>
    </>
  );
}
